import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Mail, RefreshCw, AlertTriangle, CheckCircle2, Send, Copy, Wrench } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin > Subscriptions tab.
 *
 * Shows every paid-tier user's subscription state and lets the admin
 * send a "start your monthly subscription" email + Checkout link to
 * the existing one-time-charged founders/growth/ascend users who never
 * had their card put on file for recurring billing.
 *
 * The default policy ("comp them for past months — they just start
 * subscribing now") is encoded by sending the start link with
 * `skip_initiation: true`. The admin can toggle that per-row when the
 * dialog opens.
 */
const STATUS_STYLES = {
  active: ['bg-emerald-500/15 text-emerald-300 border-emerald-500/30', 'Active'],
  trialing: ['bg-sky-500/15 text-sky-300 border-sky-500/30', 'Trialing'],
  past_due: ['bg-amber-500/15 text-amber-300 border-amber-500/30', 'Past due'],
  canceled: ['bg-rose-500/15 text-rose-300 border-rose-500/30', 'Canceled'],
  none: ['bg-white/5 text-white/60 border-white/10', 'None'],
};

function StatusPill({ status, needsBackfill }) {
  if (needsBackfill) {
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-medium border bg-amber-500/15 text-amber-300 border-amber-500/30 inline-flex items-center gap-1">
        <AlertTriangle className="w-3 h-3" /> Needs backfill
      </span>
    );
  }
  const [cls, label] = STATUS_STYLES[status] || STATUS_STYLES.none;
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}>{label}</span>;
}

export default function SubscriptionsTab({ getAuthHeader }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [onlyBackfill, setOnlyBackfill] = useState(true);
  const [sendingFor, setSendingFor] = useState(null);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/admin/subscriptions/overview`, {
        headers: getAuthHeader(),
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendStartLink = async (row, opts = { skipInitiation: true }) => {
    setSendingFor(row.id);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/subscriptions/send-start-link`,
        {
          user_id: row.id,
          origin_url: window.location.origin,
          skip_initiation: opts.skipInitiation,
        },
        { headers: getAuthHeader() },
      );
      const { email_status, url, to } = r.data;
      if (email_status === 'sent') {
        toast.success(`Start link emailed to ${to}`);
      } else {
        // Email failed (Resend domain not verified for this recipient) — surface the URL so admin can copy.
        try {
          await navigator.clipboard.writeText(url);
          toast.success(`Email failed for ${to} — Checkout URL copied to clipboard`);
        } catch {
          toast.message(`Email failed. URL: ${url}`);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send start link');
    } finally {
      setSendingFor(null);
    }
  };

  const visible = rows.filter((r) => {
    if (onlyBackfill && !r.needs_backfill) return false;
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      (r.email || '').toLowerCase().includes(f) ||
      (r.name || '').toLowerCase().includes(f) ||
      (r.membership_tier || '').toLowerCase().includes(f)
    );
  });

  const backfillCount = rows.filter((r) => r.needs_backfill).length;
  const activeCount = rows.filter((r) => r.subscription_status === 'active' && r.stripe_subscription_id).length;

  return (
    <div className="space-y-4" data-testid="subscriptions-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-playfair font-bold text-foreground flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-gold" /> Subscriptions
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Monthly recurring billing per member. Send a one-click "start subscription" link to any one-time-charged user.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRows} data-testid="refresh-subscriptions-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      <Card className="wm-card border-amber-500/30 bg-amber-500/5">
        <CardContent className="p-4 flex items-start gap-3">
          <Wrench className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-100">
              Stripe checkout broken? Click to re-seed
            </p>
            <p className="text-xs text-amber-100/70 mt-0.5">
              Forces a fresh sync of your Stripe Products + Prices into this environment's database.
              Safe to run anytime — idempotent. Use this if members see "Stripe membership prices not seeded".
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-500/40 text-amber-100 hover:bg-amber-500/10"
            data-testid="reseed-stripe-btn"
            onClick={async () => {
              try {
                const r = await axios.post(
                  `${API_URL}/api/admin/subscriptions/reseed`,
                  {},
                  { headers: getAuthHeader() },
                );
                if (r.data.ok) {
                  toast.success(
                    `Re-seeded ${r.data.tiers_resolved.length} tiers: ${r.data.tiers_resolved.join(', ')}`,
                  );
                } else {
                  toast.error(r.data.hint || 'Re-seed failed — see browser console');
                  console.error('Reseed diagnostic:', r.data);
                }
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Re-seed failed');
              }
            }}
          >
            Re-seed Stripe products
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="wm-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total paid-tier</p>
            <p className="text-2xl font-bold text-foreground" data-testid="metric-total">{rows.length}</p>
          </CardContent>
        </Card>
        <Card className="wm-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">On recurring</p>
            <p className="text-2xl font-bold text-emerald-300" data-testid="metric-active">{activeCount}</p>
          </CardContent>
        </Card>
        <Card className="wm-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Needs backfill</p>
            <p className="text-2xl font-bold text-amber-300" data-testid="metric-backfill">{backfillCount}</p>
          </CardContent>
        </Card>
        <Card className="wm-card">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Past due</p>
            <p className="text-2xl font-bold text-rose-300" data-testid="metric-past-due">
              {rows.filter((r) => r.subscription_status === 'past_due').length}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="wm-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-foreground text-base">Members</CardTitle>
          <div className="flex items-center gap-3">
            <label className="text-xs text-muted-foreground inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyBackfill}
                onChange={(e) => setOnlyBackfill(e.target.checked)}
                className="accent-amber-500"
                data-testid="only-backfill-checkbox"
              />
              Show only "needs backfill"
            </label>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by email or name…"
              className="h-8 w-56 bg-[#0a0a12] border-white/10 text-sm"
              data-testid="subscriptions-search"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-sm text-muted-foreground text-center">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">
              {onlyBackfill
                ? '🎉 Everyone is on recurring billing. No backfill needed.'
                : 'No matching users.'}
            </p>
          ) : (
            <div className="divide-y divide-white/5">
              {visible.map((r) => (
                <div
                  key={r.id}
                  className="px-4 py-3 grid grid-cols-12 items-center gap-3 text-sm"
                  data-testid={`sub-row-${r.id}`}
                >
                  <div className="col-span-4 min-w-0">
                    <p className="text-foreground truncate">{r.name || r.email}</p>
                    <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">{r.membership_tier}</span>
                  </div>
                  <div className="col-span-3">
                    <StatusPill status={r.subscription_status} needsBackfill={r.needs_backfill} />
                  </div>
                  <div className="col-span-3 flex justify-end gap-1.5">
                    {r.needs_backfill && (
                      <Button
                        size="sm"
                        disabled={sendingFor === r.id}
                        onClick={() => sendStartLink(r, { skipInitiation: true })}
                        className="bg-gold-gradient text-black font-semibold hover:shadow-gold-glow text-xs"
                        data-testid={`send-start-link-btn-${r.id}`}
                        title="Email a Stripe Checkout link to start recurring billing (initiation fee waived)"
                      >
                        {sendingFor === r.id ? (
                          <>Sending…</>
                        ) : (
                          <>
                            <Send className="w-3 h-3 mr-1" /> Send start link
                          </>
                        )}
                      </Button>
                    )}
                    {r.stripe_subscription_id && (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-300/80" title={r.stripe_subscription_id}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> On recurring
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground flex items-start gap-1.5">
        <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        Start-link emails are sent via Resend. If your sending domain isn't DNS-verified yet, the Checkout URL is copied to your clipboard instead so you can paste it manually.
      </p>
    </div>
  );
}
